import {
  FEATURE_FLAG_META_BY_KEY,
  KNOWN_FEATURE_FLAG_KEYS,
  STAFF_OVERRIDE_FLAGS,
  type FeatureFlagMap,
} from "encamp-shared/src/featureFlags";
import { createContext, useContext, useMemo } from "react";

import { useCurrentUser } from "./useCurrentUser";
import { useTenant } from "./useTenant";

/** A single tenant's feature-flag config row (key → enabled). */
type TenantConfig = { key: string; value: boolean };

/**
 * Build the resolved flag map from a tenant's configs, applying flag defaults and the
 * staff-override flags. Shared by useFeatureFlags and the tenant-scoped provider so
 * both compute flags identically.
 */
export function buildFeatureFlagMap(
  tenantConfigs: readonly TenantConfig[] | undefined,
  isStaff: boolean
): FeatureFlagMap {
  const valueByKey = new Map((tenantConfigs ?? []).map((config) => [config.key, config.value]));
  const flagMap = Object.fromEntries(
    KNOWN_FEATURE_FLAG_KEYS.map((key) => [
      key,
      valueByKey.get(key) ?? FEATURE_FLAG_META_BY_KEY[key].defaultValue,
    ])
  ) as FeatureFlagMap;
  if (isStaff) {
    for (const flag of STAFF_OVERRIDE_FLAGS) {
      flagMap[flag] = true;
    }
  }
  return flagMap;
}

/**
 * Override the ambient (current-tenant) flags for a subtree. Used by the staff Manifest
 * Review queue, which mounts customer manifest UIs for OTHER tenants and must render
 * their flags — not the staff user's current/session tenant. Null = no override.
 *
 * This map is built with the staff override applied (so staff see the reviewed
 * tenant's flags the way staff see everything). For surfaces that must mirror a
 * backend gate on the *stored* value, use {@link StoredFeatureFlagOverrideContext}
 * via {@link useStoredFeatureFlags} instead.
 */
export const FeatureFlagOverrideContext = createContext<FeatureFlagMap | null>(null);

/**
 * Companion to {@link FeatureFlagOverrideContext} carrying the reviewed tenant's
 * flags WITHOUT the staff override — i.e. the stored `TenantConfig` values. Lets
 * {@link useStoredFeatureFlags} reflect the real stored row even inside the staff
 * Manifest Review subtree (where a staff-override flag like `waste-module` would
 * otherwise read `true` regardless of the reviewed tenant's actual config).
 */
export const StoredFeatureFlagOverrideContext = createContext<FeatureFlagMap | null>(null);

export function useFeatureFlags(): {
  featureFlags: FeatureFlagMap;
  loading: boolean;
} {
  const override = useContext(FeatureFlagOverrideContext);
  const { tenant, loading: tenantLoading } = useTenant();
  const { isStaff, loading: userLoading } = useCurrentUser();

  const featureFlags = useMemo(
    () => buildFeatureFlagMap(tenant?.tenantConfigs, isStaff),
    [tenant?.tenantConfigs, isStaff]
  );

  if (override) return { featureFlags: override, loading: false };
  return { featureFlags, loading: tenantLoading || userLoading };
}

/**
 * Resolved flags WITHOUT the staff override — reflects the tenant's *stored* config.
 *
 * Use when a UI affordance must match a backend gate that reads the stored
 * `TenantConfig` value rather than the staff-override view. For example, manifest
 * ingestion is gated on the real `waste-module` row (`canProcessManifests` /
 * `buildDocumentWhereClause`), so a staff-overridden `waste-module` would show an
 * upload/reprocess action that the backend then no-ops. In a tenant-flag override
 * subtree (staff Manifest Review) we read the dedicated stored override
 * ({@link StoredFeatureFlagOverrideContext}) — NOT the staff-overridden override
 * map — so the reviewed tenant's actual stored row is reflected even for
 * staff-override flags like `waste-module`.
 */
export function useStoredFeatureFlags(): {
  featureFlags: FeatureFlagMap;
  loading: boolean;
} {
  const storedOverride = useContext(StoredFeatureFlagOverrideContext);
  const { tenant, loading: tenantLoading } = useTenant();

  const featureFlags = useMemo(
    () => buildFeatureFlagMap(tenant?.tenantConfigs, false),
    [tenant?.tenantConfigs]
  );

  if (storedOverride) return { featureFlags: storedOverride, loading: false };
  return { featureFlags, loading: tenantLoading };
}
